import React, { useState } from "react";
import { useHandleFlagChange } from "../../hooks/useHandleFlagChange";
import {
  StyledTable,
  TableContainer,
} from "../../components/tables/styledComponents";
import { buildScreenerDataSource, getColumnTypeFromKeyOrValue } from "./utils";
import { SCREENER_BASE_COLUMNS, FLAG_OPTIONS_COLUMN } from "./constants";
import { Table } from "antd";
import {
  CellType,
  CompanyMetric,
  ScreenerDataSourceType,
} from "../../models/tableModels";
import { ColumnType } from "antd/es/table";
import { ColumnType as CustomColumnType } from "../../models/tableModels";
import {
  CustomTableCell,
  CustomTableCellProps,
} from "../../components/tables/CustomTableCell";
import { MESSAGES } from "../../messages";
import {
  ScreenerConfigType,
  PaginationConfig,
  SortType,
  HoldingCompaniesMetricsRequest,
  QueryFilterType,
} from "../../types/ScreenerTypes";
import { SorterResult } from "antd/es/table/interface";
import { AnyObject } from "antd/es/_util/type";
import { FlagKey, SubjectType } from "../../models/workflowModels";
import {
  buildFlagOptions,
  FLAG_COLOR_TO_STATUS,
  FLAG_STATUS_TO_FLAG_KEY,
} from "../../components/visuals/StatusFlag";
import { Filter } from "../../types/PageFilterTypes";
import { FlagColor, FlagStatus } from "../../enums/Status";
import { formatMetricFilter } from "../../utils/PageFilterUtils";
import { MinMaxKey } from "../../components/tables/NumericFilter";

export const ScreenerTable = ({
  data,
  config,
  handlePagination,
  parameters,
  handleSort,
  handleMetricFilterUpdate,
  isLoading,
  tableKey,
  dynamicColumns,
}: ScreenerTableProps): JSX.Element => {
  const [flagOptions, setFlagOptions] = useState<{
    [key: string]: Filter[];
  } | null>(null);
  const { handleFlagChange } = useHandleFlagChange(SubjectType.HOLDINGS);
  const currentPage = parameters.pagination.page;
  const dataSource = buildScreenerDataSource(data);
  const totalRecords: number = config.holding_companies.length;

  const columns = [
    ...SCREENER_BASE_COLUMNS,
    ...dynamicColumns,
    ...FLAG_OPTIONS_COLUMN,
  ];

  /**
   * Retrieves custom cell data based on comparisons of column and value.
   * @param record - Data source containing fund information.
   * @param column - Column option indicating the data being compared.
   * @param value - The value to be compared.
   * @returns Custom table cell properties including type, value, and font color.
   */
  const getCustomCellData = (
    record: ScreenerDataSourceType,
    column: ColumnType<ScreenerDataSourceType>,
    value: CellType
  ): CustomTableCellProps => {
    const columnType: CustomColumnType = getColumnTypeFromKeyOrValue(
      column.key as string,
      value,
      isLoading
    );
    const cellType = columnType.type;
    return {
      type: cellType,
      value: value,
    };
  };

  const injectFlagHandler = (
    record: ScreenerDataSourceType
  ): ScreenerDataSourceType => {
    const flags = record.flagsDropdown;
    if (flagOptions) {
      flags.options =
        flagOptions[record.compass_cid as string] || flags.options;
    }
    flags.options.forEach((option) => {
      option.selectorName = record.compass_cid as string;
    });
    flags.onSelectOption = updateFlag;
    record.flagsDropdown = flags;
    return record;
  };

  const updateFlag = (flagOption: Filter): void => {
    handleFlagChange(flagOption);
    const flagStatus: FlagStatus =
      FLAG_COLOR_TO_STATUS[flagOption.key as FlagColor];
    const flagKey: FlagKey = FLAG_STATUS_TO_FLAG_KEY[flagStatus];
    setFlagOptions((prev) => ({
      ...prev,
      [flagOption.selectorName as string]: buildFlagOptions(flagKey),
    }));
  };

  const handleTableChange = (
    pagination: PaginationConfig,
    filters: Record<string, string[] | MinMaxKey>,
    sorter: SorterResult<AnyObject>
  ): void => {
    let sort: SortType | null = null;

    if (sorter.order) {
      const baseColumn = SCREENER_BASE_COLUMNS.find(
        (col) => col.key === sorter.columnKey
      );

      const metricColumn = dynamicColumns.find(
        (col) => col.key === sorter.columnKey
      );

      const sortKey = baseColumn?.originalKey ?? sorter.columnKey;

      sort = {
        key: sortKey as string,
        sort: sorter.order === "ascend" ? "asc" : "desc",
        is_metric_sort: !!metricColumn,
      };
    }

    handleSort(sort);
    handlePagination(pagination.current || 1);
    handleMetricFilterUpdate(formatMetricFilter(filters));
  };

  return (
    <TableContainer>
      <StyledTable
        key={tableKey}
        rowKey="key"
        pagination={{
          pageSize: Math.max(40, dataSource.length),
          total: totalRecords,
          showSizeChanger: false,
          current: currentPage,
          showTotal: (total, range) =>
            `Showing ${range[0]}-${range[1]} of ${total} records`,
        }}
        scroll={{ y: "min(58vh, 600px)", x: "max-content" }}
        hasEveryCellBorderBottom={true}
        dataSource={dataSource}
        isLoading={isLoading}
        onChange={(pagination, filters, sorter): void =>
          handleTableChange(
            pagination as PaginationConfig,
            filters as Record<string, string[] | MinMaxKey>,
            sorter as SorterResult<AnyObject>
          )
        }
      >
        {columns.map((column) => (
          <Table.Column
            key={column.key}
            title={column.title}
            fixed={column.fixed}
            dataIndex={column.dataIndex}
            filters={column.filters}
            filterDropdown={column.filterDropdown}
            onFilter={column.onFilter}
            sorter={column.sorter ? true : false}
            render={(
              value: CellType,
              record: ScreenerDataSourceType
            ): JSX.Element => (
              <CustomTableCell
                {...getCustomCellData(
                  injectFlagHandler(record),
                  column,
                  value || MESSAGES.NOT_AVAILABLE
                )}
              />
            )}
          />
        ))}
      </StyledTable>
    </TableContainer>
  );
};

type ScreenerTableProps = {
  tableKey: number;
  data: CompanyMetric[];
  config: ScreenerConfigType;
  parameters: HoldingCompaniesMetricsRequest;
  handlePagination: (page: number) => void;
  handleSort: (sort: SortType | null) => void;
  handleMetricFilterUpdate: (filters: QueryFilterType[]) => void;
  isLoading: boolean;
  dynamicColumns: ColumnType<ScreenerDataSourceType>[];
};
